import React, { useEffect } from 'react';
import {
  Button,
  Column,
  Flex,
  Heading,
  Row,
  Stack,
} from '@grubhubprod/gh-cookbook-react';
import { useFormContext } from 'react-hook-form/dist/index.ie11';
import { useParams } from 'react-router-dom';
import PageSection from 'src/components/enterprise/layout/PageSection';
import { EmailServerValidatedControl } from 'src/components/common/EmailInput/EmailServerValidatedControl';
import { BasePhoneNumberInput } from 'src/components/common/BasePhoneNumberInput/BasePhoneNumberInput';
import { InternalIdControl } from 'src/components/common/InternalIdControl/InternalIdControl';
import { SsoIdentifierTypes } from 'src/types/Corp';
import { useGetCorpData } from 'src/common/features/Corp/hooks/useGetCorpData';
import { SsoUniqueIdentifierInput } from 'src/components/common/SsoUniqueIdentifierInput/SsoUniqueIdentifierInput';
import { FirstNameControl } from 'src/components/common/FirstNameControl/FirstNameControl';
import { LastNameControl } from 'src/components/common/LastNameControl/LastNameControl';

export interface IAddEditParentAdminFormProps {
  onSubmit: () => void;
  onCancel: () => void;
  submitButtonText?: string;
  isEditing?: boolean;
}

export const AddEditParentAdminForm: React.FC<IAddEditParentAdminFormProps> = ({
  onSubmit,
  onCancel,
  submitButtonText = 'Add new admin',
  isEditing,
}) => {
  const { corpid: corpId } = useParams<{ corpid: string }>();
  const { data: corpData } = useGetCorpData(corpId);

  const {
    formState: { isDirty },
    register,
    setValue,
  } = useFormContext();

  useEffect(() => {
    register('corpSsoIdentifierType');
  }, [register]);

  useEffect(() => {
    if (corpData?.sso_identifier) {
      setValue('corpSsoIdentifierType', corpData.sso_identifier);
    }
  }, [corpData?.sso_identifier, setValue]);

  const onAddEditParentAdminCancel = (): void => onCancel();

  return (
    <>
      <Stack y="md">
        <PageSection>
          <Row>
            <Column phone={10}>
              <Stack y="md">
                <Heading variant="h3">Parent admin information</Heading>
              </Stack>
              <Row gutter={12}>
                <Column gutter={12} phone={6}>
                  <Stack y="sm">
                    <FirstNameControl name="firstName" id="firstName" />
                  </Stack>
                </Column>
                <Column gutter={12} phone={6}>
                  <Stack y="sm">
                    <LastNameControl name="lastName" id="lastName" />
                  </Stack>
                </Column>
                <Column gutter={12} phone={6}>
                  <Stack y="sm">
                    <EmailServerValidatedControl
                      name="email"
                      id="email"
                      disabled={isEditing}
                      disabledInfo="Need to change this email? Go back and select “Deactivate”, then add the admin again with your preferred email. Contact your CSM with any questions."
                    />
                  </Stack>
                </Column>
                <Column gutter={12} phone={6}>
                  <Stack y="sm">
                    <BasePhoneNumberInput name="phoneNumber" id="phoneNumber" />
                  </Stack>
                </Column>
                <Column gutter={12} phone={6}>
                  <InternalIdControl
                    name="internalId"
                    id="internalId"
                    rules={{
                      required: {
                        value:
                          corpData?.sso_identifier ===
                          SsoIdentifierTypes.INTERNAL_ID,
                        message: 'Internal ID is required',
                      },
                    }}
                  />
                </Column>
                {corpData?.sso_enabled &&
                  corpData?.sso_identifier === SsoIdentifierTypes.OTHER && (
                    <Column gutter={12} phone={6}>
                      <SsoUniqueIdentifierInput
                        name="ssoUniqueIdentifier"
                        id="ssoUniqueIdentifier"
                        label={corpData.sso_unique_identifier}
                      />
                    </Column>
                  )}
              </Row>
            </Column>
          </Row>
        </PageSection>
      </Stack>
      <Flex justifyContent="flex-end">
        <Stack x="md">
          <Button
            variant="secondary"
            buttonSize="medium"
            onClick={onAddEditParentAdminCancel}
          >
            Cancel
          </Button>
        </Stack>
        <Button
          variant="primary"
          buttonSize="medium"
          onClick={onSubmit}
          disabled={!isDirty}
        >
          {submitButtonText}
        </Button>
      </Flex>
    </>
  );
};
